/*
 * Copyright (c) 2021 OTB Africa <https://otbafrica.com>
 *
 * Created Date: Friday, October 15th 2021, 12:03:44 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useAPIRequest, UseAPIRequestArgs, APIMakeRequestFncArgs } from "lib/api/hooks";
import { useDeepEffect } from "lib/deep_hooks";
import { QueryParameters } from "lib/types";

/**
 *
 * Use this hook when you want to get a list of values from an API List endpoint without pagination.
 *
 * @param apiServicePath The API Service to call
 * @param dependencyList The list of items that should result in any useEffect hooks running during a re-render. Dependency list must always be a list of either primitive values or memoized objects
 * @returns array of items, count and makeRequest hook state i.e. isMakingAPIRequest and isAPIError
 *
 */

export interface UseFetchListArgs<T>
  extends UseAPIRequestArgs<T[], T, T>,
    Pick<APIMakeRequestFncArgs<T[], T, T>, "apiQueryParams" | "apiPathParams"> {
  dependencyList?: any[];
}

type FetchListFncOptions<T> = Omit<APIMakeRequestFncArgs<T>, "setValidAPIResponse">;

export const useAPIFetchList = <T>(args: UseFetchListArgs<T>) => {
  const { dependencyList = [], apiQueryParams = {}, apiPathParams = [] } = args;

  const [items, setItems] = React.useState<T[]>([]);
  const [apiTotalItems, setAPITotalItems] = React.useState<number>(0);

  // Fetch data afresh whenever the globalState or other passed dependencies change
  const { makeRequest, makeRequestCleanUp, ...othermakeRequestHookValues } = useAPIRequest<T[], T, T>({ ...args });

  const fetchList = React.useCallback(
    (options?: FetchListFncOptions<T>) => {
      const resolvedPathParams = [...apiPathParams, ...(options?.apiPathParams ?? [])];

      const resolvedQueryParams = { ...apiQueryParams, ...options?.apiQueryParams } as QueryParameters<T>;

      return makeRequest({
        ...args,
        apiPathParams: resolvedPathParams,
        apiQueryParams: resolvedQueryParams,
        setValidAPIResponse: setItems,
      });
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [makeRequest, JSON.stringify(apiQueryParams), JSON.stringify(apiPathParams)]
  );

  React.useEffect(() => {
    if (args.makeRequestOnInitialMount) return fetchList(); // Returning the makeRequest cleanup function so that the cleanup can be called by useEffect.
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencyList, fetchList]);

  useDeepEffect(() => {
    setAPITotalItems(items.length);
  }, [items]);

  return {
    apiTotalItems,
    items,
    fetchList,
    ...othermakeRequestHookValues,
  };
};
